import type { CloudUser } from './AuthContext';
import type { ResourceManifestDiff } from '../platform/resourceCache';

export type AuthenticationAcceptSource = 'normal' | 'logout-recovery';

export class AuthTransitionGate {
  private logoutActive = false;

  beginLogout(): boolean {
    if (this.logoutActive) return false;
    this.logoutActive = true;
    return true;
  }

  canRefresh(): boolean {
    return !this.logoutActive;
  }

  canAccept(source: AuthenticationAcceptSource): boolean {
    return !this.logoutActive || source === 'logout-recovery';
  }

  finishLogout(): void {
    this.logoutActive = false;
  }

  isLogoutActive(): boolean {
    return this.logoutActive;
  }
}

export function authorizationFingerprint(user: CloudUser): string {
  return `${user.id}:${user.role}`;
}

export function resourceAuthorizationFingerprint(user: CloudUser): string {
  return `${user.authorizationRevision}:${[...new Set(user.featureKeys)].sort().join(',')}`;
}

export function authorizationTransition(
  previousBoundary: string | null,
  previousResources: string | null,
  nextUser: CloudUser,
): { boundaryChanged: boolean; resourcesChanged: boolean } {
  return {
    boundaryChanged: previousBoundary !== authorizationFingerprint(nextUser),
    resourcesChanged: previousResources !== resourceAuthorizationFingerprint(nextUser),
  };
}

export function modelIdsInvalidatedByManifest(diff: ResourceManifestDiff): Set<string> {
  return new Set(diff.revoked
    .filter((entry) => entry.kind === 'model-thumbnail')
    .map((entry) => entry.id.split(':')[1])
    .filter((id): id is string => Boolean(id)));
}

export type LogoutRecovery<T> =
  | { kind: 'authenticated'; user: T }
  | { kind: 'logged-out' }
  | { kind: 'unknown'; error: unknown };

export async function verifyLogoutRecovery<T>(
  loadCurrentUser: () => Promise<T>,
  isUnauthorized: (error: unknown) => boolean,
): Promise<LogoutRecovery<T>> {
  try {
    return { kind: 'authenticated', user: await loadCurrentUser() };
  } catch (error) {
    return isUnauthorized(error)
      ? { kind: 'logged-out' }
      : { kind: 'unknown', error };
  }
}

export function retainSelectedModelsAfterManifest<T extends { id: string }>(
  selected: T[],
  diff: ResourceManifestDiff,
): T[] {
  const invalidatedIds = modelIdsInvalidatedByManifest(diff);
  return invalidatedIds.size
    ? selected.filter((model) => !invalidatedIds.has(model.id))
    : selected;
}

export function reconcileSelectedModels<T extends { id: string }>(selected: T[], catalog: T[]): T[] {
  const modelsById = new Map(catalog.map((model) => [model.id, model]));
  return selected.flatMap((model) => {
    const current = modelsById.get(model.id);
    return current ? [current] : [];
  });
}

export async function settleLogoutBoundary(
  logoutRequest: () => Promise<unknown>,
  cacheCleanup: Promise<unknown>,
): Promise<void> {
  try {
    await logoutRequest();
  } finally {
    await cacheCleanup;
  }
}

export async function completeLogoutBoundary(
  logoutRequest: () => Promise<unknown>,
  cacheCleanup: Promise<unknown>,
  onSuccess: () => void | Promise<void>,
  onFailure: (error: unknown) => void | Promise<void>,
): Promise<void> {
  try {
    await settleLogoutBoundary(logoutRequest, cacheCleanup);
    await onSuccess();
  } catch (error) {
    await onFailure(error);
    throw error;
  }
}
